'use client';

import React, { useState } from 'react';
import { Section } from '@/components/ui/Section';
import { RichText } from '@/components/ui/RichText';
import styles from './FAQSection.module.css';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSectionProps {
  label?: string;
  title: string;
  subtitle: string;
  questions: FAQItem[];
  background?: 'primary' | 'secondary';
}

/**
 * FAQSection Component
 * Reusable FAQ component with accordion design
 * Used across all pages (homepage, kiosk, QR, tablet, etc.)
 */
export function FAQSection({ label, title, subtitle, questions, background = 'primary' }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Section background={background} paddingY="none">
      <div className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          {label && <span className={styles.sectionLabel}>{label}</span>}
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.faqList}>
          {questions.map((item, index) => (
            <div
              key={index}
              className={`${styles.faqItem} ${openIndex === index ? styles.open : ''}`}
            >
              <button
                className={styles.question}
                onClick={() => toggleQuestion(index)}
                aria-expanded={openIndex === index}
              >
                <span className={styles.questionText}>{item.question}</span>
                <span className={styles.icon}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id={`faqGradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ff9966" />
                        <stop offset="100%" stopColor="#ff5e62" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M12 5V19M5 12H19"
                      stroke={`url(#faqGradient-${index})`}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <div className={styles.answerWrapper}>
                <div className={styles.answer}>
                  <RichText text={item.answer} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </Section>
  );
}

